/**
 * Mensagens fixadas de um canal. Uma lista compacta, só de leitura — quem tem
 * MANAGE_MESSAGES pode desafixar ali mesmo. Não é o sítio para editar nem
 * reagir: é o índice do que ficou marcado como importante.
 */

import type { Message, Snowflake } from "@greenvoice/shared";
import { Permission } from "@greenvoice/shared";
import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { formatMessageStamp } from "../lib/format";
import { idCompare } from "../lib/ids";
import { can, selfPermissions } from "../store/selectors";
import { useStore } from "../store/store";
import { Avatar } from "./Avatar";
import { IconClose, IconPin } from "./Icons";
import { RichText } from "./RichText";

export function PinsPanel({
  channelId,
  serverId,
  onClose,
}: {
  channelId: Snowflake;
  serverId: Snowflake | null;
  onClose: () => void;
}) {
  const state = useStore();
  const channel = state.channels[channelId];
  const canManage = can(selfPermissions(state, serverId, channel), Permission.MANAGE_MESSAGES);

  const [pins, setPins] = useState<Message[]>([]);
  const [busy, setBusy] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    setBusy(true);
    setError(null);
    api.messages
      .pins(channelId)
      .then((list) => {
        if (alive) setPins([...list].sort((a, b) => idCompare(b.id, a.id)));
      })
      .catch((caught) => {
        if (alive) setError((caught as Error).message);
      })
      .finally(() => {
        if (alive) setBusy(false);
      });
    return () => {
      alive = false;
    };
  }, [channelId]);

  async function unpin(id: Snowflake) {
    try {
      await api.messages.unpin(channelId, id);
      setPins((prev) => prev.filter((m) => m.id !== id));
    } catch (caught) {
      setError((caught as Error).message);
    }
  }

  return (
    <div className="modal" role="dialog" aria-modal="true" aria-label="Mensagens fixadas">
      <div className="modal__panel">
        <header className="modal__head">
          <h2>
            <IconPin size={18} /> Mensagens fixadas
          </h2>
          <button type="button" className="icon-button" onClick={onClose} title="Fechar">
            <IconClose size={18} />
          </button>
        </header>

        {busy ? (
          <p className="modal__hint">Carregando…</p>
        ) : pins.length === 0 ? (
          <p className="modal__hint">Nada fixado aqui ainda.</p>
        ) : (
          <ul className="pins">
            {pins.map((message) => {
              const name = message.member?.nickname ?? message.author.displayName ?? message.author.username;
              return (
                <li key={message.id} className="pins__item">
                  <Avatar user={message.author} overrideUrl={message.member?.avatarUrl ?? null} size={32} />
                  <div className="pins__body">
                    <div className="pins__meta">
                      <strong>{name}</strong>
                      <time dateTime={message.createdAt}>{formatMessageStamp(message.createdAt)}</time>
                      {canManage ? (
                        <button type="button" className="link pins__unpin" onClick={() => void unpin(message.id)}>
                          Desafixar
                        </button>
                      ) : null}
                    </div>
                    {message.content ? (
                      <RichText content={message.content} />
                    ) : message.attachments.length > 0 ? (
                      <p className="pins__attachment">
                        {message.attachments.length === 1 ? "1 anexo" : `${message.attachments.length} anexos`}
                      </p>
                    ) : null}
                  </div>
                </li>
              );
            })}
          </ul>
        )}

        {error ? (
          <p className="auth__error" role="alert">
            {error}
          </p>
        ) : null}
      </div>
    </div>
  );
}
